import {TooltipDataCallback} from 'common/charts/visualization/bar-viz/types';
import {
  DateKeyedFunnelSegments,
  FunnelBookmark,
  FunnelMedianDataSegment,
  FunnelSegment,
  FunnelStep,
} from 'common/report/funnels/models/types';
import {getFunnelStep, getMedian} from 'common/report/funnels/models/bar-chart/utils';
import {
  getCalendarDateRangeLabels,
  getCalendarDateRanges,
  getMedianComparator,
  getRenamedOrderedSegmentSteps,
} from 'common/report/funnels/models/utils';
import {FunnelsQuery} from 'common/report/funnels/models/funnels-query';
import {convertToFlatSavedLegend, convertToNonFlatSavedLegend} from 'common/report/funnels/util/colors';
import {TooltipBulletStyle, TooltipItemType, TooltipRow} from 'common/components/chart-tooltip/types';
import {createFunnelDataWithRename, usesFlatSelectedSegments} from 'common/report/funnels/util';
import {FunnelQueryResponse} from 'common/report/funnels/models/api-types';
import {SegmentItem} from 'common/charts/contexts/segment-filter-controller';
import {SavedLegend} from 'common/report/constants';
import {CalendarDateRange} from 'common/widgets/date-range-picker/models/types';

// TODO: Move this to a more common file since all the different chart types are using it now, instead of just
// the aggregate charts.
/**
 * Turns each series into a FunnelSegment[], ordered according to the query, with segments renamed as-needed.
 * Also extracts items for a segment legend, and the saved/selected segments
 */
export function processFunnelForChartWidget(
  bookmark: FunnelBookmark,
  funnelsResponse: FunnelQueryResponse,
  projectId: string,
  projectAsOfDate: string,
  stepIndex: number | null, // null is a valid stepIndex for ttc
  medianAccessor: keyof FunnelStep,
  projectTimezone,
): {
  stateUpdates: {
    stepIndex: number;
    query: FunnelsQuery;
    formattedCompareData: null | FunnelSegment[];
    formattedLiftData: null | FunnelSegment[];
    dateRangeLabels: string[];
    dateRanges: CalendarDateRange[];
    formattedData: FunnelSegment[];
  };
  legendSegmentItems: SegmentItem[];
  savedLegend: SavedLegend[];
} {
  const query = FunnelsQuery.fromBookmark(
    bookmark,
    // TODO - change the types for funnelsquery to take string projectIDs in a separate refactor
    parseInt(projectId, 10),
  );

  const segmentData: DateKeyedFunnelSegments[] = createFunnelDataWithRename(funnelsResponse.data, query);
  const hasComparisonData = !!funnelsResponse.compare_data;
  const comparisonSegmentData: DateKeyedFunnelSegments[] = hasComparisonData
    ? createFunnelDataWithRename(funnelsResponse.compare_data, query)
    : null;

  const hasLiftData = !!funnelsResponse.lift_data;
  const liftSegmentData: DateKeyedFunnelSegments[] = hasLiftData
    ? createFunnelDataWithRename(funnelsResponse.lift_data, query)
    : null;

  let formattedCompareData = null;
  let formattedLiftData = null;

  if (hasComparisonData) {
    formattedCompareData = getRenamedOrderedSegmentSteps(
      query,
      comparisonSegmentData,
      getMedianComparator(stepIndex, medianAccessor),
    );
  }

  if (hasLiftData) {
    formattedLiftData = getRenamedOrderedSegmentSteps(
      query,
      liftSegmentData,
      getMedianComparator(stepIndex, medianAccessor),
    );
  }

  const formattedData = getRenamedOrderedSegmentSteps(
    query,
    segmentData,
    getMedianComparator(stepIndex, medianAccessor),
  );

  // TODO: when bookmark.selectedSegments is finally removed, savedLegend logic can be drastically simplified and moved to another method
  let savedLegend: SavedLegend[] = null;
  if (bookmark?.savedLegend && bookmark?.savedLegend?.length > 0) {
    savedLegend = bookmark.savedLegend;
  } else if (bookmark?.selectedSegments?.length) {
    if (usesFlatSelectedSegments(bookmark)) {
      savedLegend = convertToFlatSavedLegend(bookmark.selectedSegments);
    } else {
      savedLegend = convertToNonFlatSavedLegend(bookmark.selectedSegments, formattedData);
    }
  }

  const stateUpdates = {
    dateRangeLabels: getCalendarDateRangeLabels(bookmark, funnelsResponse, projectAsOfDate, projectTimezone),
    dateRanges: getCalendarDateRanges(bookmark, funnelsResponse, projectAsOfDate, projectTimezone),
    formattedData,
    formattedCompareData,
    formattedLiftData,
    query,
    stepIndex,
  };

  const legendSegmentItems: SegmentItem[] = formattedData.map((funnelSegment) => ({
    id: funnelSegment.prop,
    label: funnelSegment.propName,
  }));

  return {
    stateUpdates,
    legendSegmentItems,
    savedLegend,
  };
}

export function generateMedianSegments({
  formattedData,
  formattedCompareData,
  medianAccessor,
  hasComparisonData,
  stepIndex,
}: {
  formattedData: FunnelSegment[];
  formattedCompareData: FunnelSegment[];
  medianAccessor: keyof FunnelStep;
  hasComparisonData: boolean;
  stepIndex: number;
}): FunnelMedianDataSegment[] {
  const segments: FunnelMedianDataSegment[] = [];

  // todo(sam) this won't work for unflattened segments
  formattedData.forEach((funnelSegment) => {
    const step = getFunnelStep(funnelSegment.steps, stepIndex);
    const median = getMedian(step, medianAccessor);

    let dataSegment: FunnelMedianDataSegment = {
      value: median || 0, // TTC and Freq charts want to display an undefined value with n/a, which the provided formatter will take care of
      label: funnelSegment.propName,
      prop: funnelSegment.prop,
      props: funnelSegment.props.map((segmentId) => segmentId.prop),
    };

    if (hasComparisonData) {
      const comparisonSegment = formattedCompareData.find(
        (segment: FunnelSegment) => segment.prop === funnelSegment.prop,
      );

      const comparisonStep = getFunnelStep(comparisonSegment.steps, stepIndex);
      const comparisonMedian = getMedian(comparisonStep, medianAccessor);

      dataSegment = {
        ...dataSegment,
        comparisonValue: comparisonMedian || 0,
      };
    }

    segments.push(dataSegment);
  });

  return segments;
}

export function createTooltipDataCallback(
  formattedCompareData,
  formatter: (label: string) => TooltipRow[],
  dateRangeLabels?: string[],
): TooltipDataCallback {
  return (segment, _bar, barSegment) => {
    return {
      ...(formattedCompareData ? {} : {'title-color': barSegment.color}),
      sections: [
        {
          rows: [
            {
              items: [
                {
                  text: segment.label,
                  type: TooltipItemType.Strong,
                },
              ],
            },
          ],
        },
        ...(formattedCompareData
          ? [
              {
                bulletStyle: TooltipBulletStyle.Solid,
                bulletColor: barSegment.color,
                rows: [
                  {
                    items: [{text: dateRangeLabels[0], type: TooltipItemType.Detail}],
                  },
                  ...formatter(`${segment.bars[0].segments[0].label}`),
                ],
              },
            ]
          : [
              {
                rows: [...formatter(`${segment.bars[0].segments[0].label}`)],
              },
            ]),
        ...(formattedCompareData
          ? [
              {}, // empty row for spacing
              {
                bulletStyle: TooltipBulletStyle.Dash,
                bulletColor: barSegment.color,
                rows: [
                  {
                    items: [{text: dateRangeLabels[1], type: TooltipItemType.Detail}],
                  },
                  ...formatter(`${segment.bars[1].segments[0].label}`),
                ],
              },
            ]
          : []),
      ],
    };
  };
}
